import * as React from 'react'
import { useEffect, useState } from 'react'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { Button, Well } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { CompoundTable, CompoundTableHeader } from '@halo/components/compound-table/compound-table'
import { Dropdown, Menu, Modal, notification, Table } from 'antd'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import './style/page-analysis-field-configurations.tab.scss'
import { PageAnalysisFieldsService } from '@halo/services/page-analysis-fields.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { snakeToHumanCase } from '@halo/_utils/string'
import { EditOutlined } from '@ant-design/icons'
import { IPageAnalysisField } from '@pushly/aqe/lib/interfaces'

export interface IPageAnalysisDetailsTabProps {
    hydratedPageAnalysisConfig: IHydratedPageAnalysisConfig
    onUpdate: (update: IHydratedPageAnalysisConfig) => void
}

function PageAnalysisFieldConfigsTab(props: IPageAnalysisDetailsTabProps) {
    const appService = Container.get(AppService)
    const pageAnalysisFieldsService: PageAnalysisFieldsService = Container.get(PageAnalysisFieldsService)
    const [isDeleting, setIsDeleting] = useState(false)

    function renderRowActions(_: any, record: IPageAnalysisField): React.ReactNode {
        if (record.page_analysis_config_id === props.hydratedPageAnalysisConfig.id) {
            return (
                <Dropdown.Button
                    size="small"
                    onClick={() => {
                        appService.route(
                            `/page-analysis-configs/${record.page_analysis_config_id}/field-configs/${record.id}`,
                        )
                    }}
                    overlay={
                        <Menu>
                            <Menu.Item onClick={() => del(record)}>
                                <span>Delete</span>
                            </Menu.Item>
                        </Menu>
                    }
                >
                    <EditOutlined />
                    <span>Edit</span>
                </Dropdown.Button>
            )
        }
    }

    function del(pageAnalysisField: IPageAnalysisField) {
        Modal.confirm({
            title: 'Delete Page Analysis Field',
            content: (
                <>
                    <p>Are you sure you want to delete this page analysis field?</p>
                </>
            ),
            onOk: async (...args: any[]) => {
                try {
                    setIsDeleting(true)
                    await pageAnalysisFieldsService.delete(props.hydratedPageAnalysisConfig.id, pageAnalysisField.id)

                    props.hydratedPageAnalysisConfig.fields = props.hydratedPageAnalysisConfig.fields.filter(
                        (f) => f.id !== pageAnalysisField.id,
                    )
                    props.onUpdate(props.hydratedPageAnalysisConfig as IHydratedPageAnalysisConfig)
                } catch (e) {
                    simpleNotification('error', e.toString())
                } finally {
                    setIsDeleting(false)
                }
            },
            onCancel: () => {},
        })
    }

    function render() {
        return (
            <>
                <Well
                    className={classnames('page-analysis-field-configurations-list', 'type-2')}
                    hideHeader={true}
                    hideFooter={true}
                    loading={!props.hydratedPageAnalysisConfig}
                >
                    {props.hydratedPageAnalysisConfig && (
                        <CompoundTable>
                            <CompoundTableHeader
                                right={
                                    <>
                                        <Button
                                            type="primary"
                                            disabled={isDeleting}
                                            onClick={() => {
                                                appService.route(
                                                    `/page-analysis-configs/${props.hydratedPageAnalysisConfig.id}/field-configs/new`,
                                                )
                                            }}
                                        >
                                            Add Field Config
                                        </Button>
                                    </>
                                }
                            />
                            <Table
                                size="small"
                                pagination={false}
                                dataSource={props.hydratedPageAnalysisConfig?.fields}
                                rowKey={(record) => record.id.toString()}
                                rowClassName={(record) =>
                                    record.page_analysis_config_id === props.hydratedPageAnalysisConfig.id
                                        ? 'local'
                                        : 'inherited'
                                }
                                locale={{
                                    emptyText: <AntdTableEmptyPlaceholder text="No Field Configs" icon="file-search" />,
                                }}
                            >
                                <Table.Column className="id" title="ID" dataIndex="id" />
                                <Table.Column className="priority" title="Priority" dataIndex="priority" />
                                <Table.Column
                                    className="field"
                                    title="Field"
                                    render={(record) => {
                                        if (record.name.indexOf('additional_fields.') === 0) {
                                            return record.name
                                        } else {
                                            return snakeToHumanCase(record.name)
                                        }
                                    }}
                                />
                                <Table.Column className="data-type" title="Data Type" dataIndex="type" />
                                <Table.Column
                                    className="strategies"
                                    title="Strategies"
                                    render={(text, record: IPageAnalysisField, idx) => {
                                        const strategyCounts = (record.strategies || []).reduce((acc: any, val) => {
                                            if (!(val.type in acc)) {
                                                acc[val.type] = 0
                                            }
                                            acc[val.type] += 1

                                            return acc
                                        }, {})

                                        return (
                                            <>
                                                {Object.keys(strategyCounts)
                                                    .sort()
                                                    .map((k) => {
                                                        return (
                                                            <span key={k}>
                                                                {k} ({strategyCounts[k]}){' '}
                                                            </span>
                                                        )
                                                    })}
                                            </>
                                        )
                                    }}
                                />
                                <Table.Column
                                    className="actions"
                                    title="Actions"
                                    width={180}
                                    render={renderRowActions}
                                />
                            </Table>
                        </CompoundTable>
                    )}
                </Well>
            </>
        )
    }

    return render()
}

export default PageAnalysisFieldConfigsTab
